import { useCallback } from 'react';
import { useSignIn } from '@clerk/nextjs';
import { isClerkAPIResponseError, isClerkError } from '@clerk/shared/error';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useCountdown } from '@/hooks/use-countdown';

import { prepareSecondFactor } from '../factors';

const schema = z.object({
  code: z.string().trim(),
});

type Props = Readonly<{
  onSubmit: () => void;
}>;

export const ClientTrust = ({ onSubmit }: Props) => {
  const { signIn, fetchStatus } = useSignIn();

  const [resendableAfter, { startCountdown, resetCountdown, isCountdownRunning }] = useCountdown({ countStart: 30 });

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      code: '',
    },
  });

  const resendCode = useCallback(async () => {
    resetCountdown();

    if (signIn.status === 'needs_client_trust') {
      await prepareSecondFactor({ strategy: 'email_code', signIn });

      startCountdown();
    }
  }, [resetCountdown, signIn, startCountdown]);

  const handleSubmit = useCallback(
    async ({ code }: z.infer<typeof schema>) => {
      const { error } = await signIn.mfa.verifyEmailCode({ code });
      if (error) {
        if (isClerkAPIResponseError(error)) {
          form.setError('code', { type: 'manual', message: error.errors[0].longMessage });
        } else if (isClerkError(error)) {
          form.setError('code', { type: 'manual', message: error.longMessage });
        }
        console.error(JSON.stringify(error, null, 2));
        return;
      }

      if (signIn.status === 'complete') {
        onSubmit();
      } else {
        // If the status is not complete, check why. User may need to
        // complete further steps.
        console.error(JSON.stringify(signIn, null, 2));
      }
    },
    [form, onSubmit, signIn],
  );

  return (
    <Card className="w-full min-w-96">
      <CardHeader>
        <CardTitle>Verify your account</CardTitle>
        <CardDescription>
          We&apos;ve sent a code to <strong>{signIn?.identifier}</strong>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form id="client-trust-form" onSubmit={form.handleSubmit(handleSubmit)}>
          <FieldGroup>
            <Controller
              control={form.control}
              name="code"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor={field.name}>Email verification code</FieldLabel>
                  <InputOTP
                    {...field}
                    id={field.name}
                    maxLength={6}
                    containerClassName="justify-center"
                    onComplete={form.handleSubmit(handleSubmit)}
                    autoFocus
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                  <FieldDescription className="flex justify-center">
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="text-muted-foreground"
                      disabled={isCountdownRunning}
                      onClick={resendCode}
                    >
                      Didn&apos;t receive a code? Resend{' '}
                      {isCountdownRunning && (
                        <>
                          (<span className="tabular-nums">{resendableAfter}</span>)
                        </>
                      )}
                    </Button>
                  </FieldDescription>
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
          </FieldGroup>
        </form>
      </CardContent>
      <CardFooter>
        <Field orientation="vertical">
          <Button type="submit" form="client-trust-form" className="w-full" disabled={fetchStatus === 'fetching'}>
            Continue
          </Button>
        </Field>
      </CardFooter>
    </Card>
  );
};
